// 「音」にまつわる漢字のリスト。
// サイトを開くたびに、この中からランダムに1字を選んで大きく表示する。
// 各字には「背景・文字の配色テーマ」と「音との結びつきを感じさせる一言メモ」を持たせる。
// 将来的に100字程度まで増やす想定なので、追記はこの配列に足すだけでよい。

export interface SoundKanjiEntry {
  /** 表示する漢字 */
  kanji: string;
  /** 音との結びつきを感じさせる一言メモ（断定ではなく、余韻のある語り） */
  memo: string;
  /** 漢字グラデーションの上側の色 */
  ink: string;
  /** 漢字グラデーションの下側の色 */
  ink2: string;
  /** 背景ベース（やや明るい側） */
  bg1: string;
  /** 背景の最も暗い部分 */
  bg2: string;
  /** 中央にほのかに灯る光の色（rgba） */
  glow: string;
  /** ライン・ヒントなどに使うアクセント色 */
  accent: string;
}

export const SOUND_KANJI: SoundKanjiEntry[] = [
  {
    kanji: '音',
    memo: '「立」と「日」を重ね、日々鳴り響く様を表すとも言われる字',
    ink: '#5eead4',
    ink2: '#0d9488',
    bg1: '#04201d',
    bg2: '#030b0a',
    glow: 'rgba(13,148,136,0.35)',
    accent: '#14b8a6',
  },
  {
    kanji: '響',
    memo: '「郷」（遠く）に「音」が届く、遠くまで響き渡る様を表す',
    ink: '#fcd34d',
    ink2: '#b45309',
    bg1: '#221704',
    bg2: '#0c0803',
    glow: 'rgba(212,164,74,0.35)',
    accent: '#d4a24a',
  },
  {
    kanji: '静',
    memo: '音が澄んでゆく、動きがすっと収まった状態を思わせる字',
    ink: '#a8c8ef',
    ink2: '#1e3a5f',
    bg1: '#0a1526',
    bg2: '#04070f',
    glow: 'rgba(59,130,196,0.3)',
    accent: '#5b8fd0',
  },
  {
    kanji: '騒',
    memo: '馬がざわめく様から転じ、音が入り乱れる様を表す',
    ink: '#fb923c',
    ink2: '#b91c1c',
    bg1: '#240d07',
    bg2: '#0d0403',
    glow: 'rgba(232,67,47,0.35)',
    accent: '#e8432f',
  },
  {
    kanji: '韻',
    memo: '音の響きの調和、そして残る余韻を思わせる字',
    ink: '#c4b5fd',
    ink2: '#6d28d9',
    bg1: '#170f28',
    bg2: '#08050f',
    glow: 'rgba(124,77,190,0.32)',
    accent: '#7c4dbe',
  },
  {
    kanji: '奏',
    memo: '両手で捧げ物をする様から、音楽を演じる意に転じた字',
    ink: '#fde68a',
    ink2: '#ca8a04',
    bg1: '#211a06',
    bg2: '#0b0902',
    glow: 'rgba(212,180,106,0.3)',
    accent: '#d4b46a',
  },
  {
    kanji: '囁',
    memo: '小さく漏れる声、ささやきを表す。耳もとへ届くほどの音',
    ink: '#ddd6fe',
    ink2: '#7c6bb5',
    bg1: '#16131f',
    bg2: '#07060c',
    glow: 'rgba(183,167,217,0.28)',
    accent: '#b7a7d9',
  },
  {
    kanji: '鳴',
    memo: '鳥が口を使って声を出す様を表す字。命の音が空にひらく',
    ink: '#86efac',
    ink2: '#15803d',
    bg1: '#07190f',
    bg2: '#030a06',
    glow: 'rgba(79,157,105,0.32)',
    accent: '#4f9d69',
  },
  {
    kanji: '声',
    memo: '人の発する音、感情がのった音を表す字',
    ink: '#fdba74',
    ink2: '#c2410c',
    bg1: '#21100a',
    bg2: '#0b0503',
    glow: 'rgba(232,133,58,0.32)',
    accent: '#e8853a',
  },
  {
    kanji: '波',
    memo: '水面の揺らぎ。音もまた波として伝わることに通じる',
    ink: '#93c5fd',
    ink2: '#1d4ed8',
    bg1: '#0a1526',
    bg2: '#03070f',
    glow: 'rgba(59,130,196,0.32)',
    accent: '#3b82c4',
  },
  {
    kanji: '風',
    memo: '自然がおのずと奏でる、目には見えない音の存在',
    ink: '#bef264',
    ink2: '#4d7c0f',
    bg1: '#10200a',
    bg2: '#060d03',
    glow: 'rgba(127,176,105,0.3)',
    accent: '#7fb069',
  },
  {
    kanji: '雷',
    memo: '空が轟く、自然界でもっとも激しく響く音',
    ink: '#a78bfa',
    ink2: '#4c1d95',
    bg1: '#100a20',
    bg2: '#05030c',
    glow: 'rgba(91,61,140,0.4)',
    accent: '#8b5cf6',
  },
  {
    kanji: '暑',
    memo: '蝉時雨に象徴される、夏の音の記憶',
    ink: '#fca5a5',
    ink2: '#b91c1c',
    bg1: '#240a0a',
    bg2: '#0d0303',
    glow: 'rgba(217,75,58,0.36)',
    accent: '#d94b3a',
  },
  {
    kanji: '寒',
    memo: 'しんと静まり返る、冬の音の記憶',
    ink: '#e0f2fe',
    ink2: '#7ea8c4',
    bg1: '#101b22',
    bg2: '#050a0d',
    glow: 'rgba(169,196,214,0.28)',
    accent: '#a9c4d6',
  },
  {
    kanji: '谺',
    memo: '山や谷に音が返る、こだまを表す字',
    ink: '#5eead4',
    ink2: '#0f766e',
    bg1: '#04201d',
    bg2: '#030b0a',
    glow: 'rgba(20,184,166,0.34)',
    accent: '#14b8a6',
  },
  {
    kanji: '鐘',
    memo: '時を告げる音。撞いたあとにも長く残る余韻を思わせる',
    ink: '#fcd34d',
    ink2: '#a16207',
    bg1: '#201704',
    bg2: '#0b0802',
    glow: 'rgba(201,154,60,0.32)',
    accent: '#c99a3c',
  },
  {
    kanji: '笛',
    memo: '息を吹き込んで鳴らす、素朴であたたかな音色',
    ink: '#d9f99d',
    ink2: '#65a30d',
    bg1: '#131d08',
    bg2: '#070c03',
    glow: 'rgba(138,163,77,0.3)',
    accent: '#8aa34d',
  },
  {
    kanji: '鼓',
    memo: '打って鳴らす、リズムの原点。からだで感じる音',
    ink: '#fca5a5',
    ink2: '#991b1b',
    bg1: '#230808',
    bg2: '#0c0303',
    glow: 'rgba(184,58,46,0.34)',
    accent: '#b83a2e',
  },
  {
    kanji: '唄',
    memo: '言葉に節をつけてうたう、暮らしの中の音',
    ink: '#fda4af',
    ink2: '#be123c',
    bg1: '#231015',
    bg2: '#0c0407',
    glow: 'rgba(232,117,106,0.32)',
    accent: '#e8756a',
  },
  {
    kanji: '夢',
    memo: '眠りの淵に漂う、おぼろげな音の気配',
    ink: '#c7d2fe',
    ink2: '#4338ca',
    bg1: '#101228',
    bg2: '#05060f',
    glow: 'rgba(90,90,168,0.34)',
    accent: '#5a5aa8',
  },
  {
    kanji: '寂',
    memo: '静けさの底に、かすかに居続ける音',
    ink: '#cbd5e1',
    ink2: '#475569',
    bg1: '#101720',
    bg2: '#05080c',
    glow: 'rgba(70,88,110,0.3)',
    accent: '#46586e',
  },
  {
    kanji: '雨',
    memo: '屋根を打つ、細かな音の粒。無数の音が重なる調べ',
    ink: '#bae6fd',
    ink2: '#0369a1',
    bg1: '#0a1a24',
    bg2: '#03090d',
    glow: 'rgba(74,122,156,0.32)',
    accent: '#4a7a9c',
  },
  {
    kanji: '川',
    memo: '絶えず流れつづける、途切れることのない音',
    ink: '#a5f3fc',
    ink2: '#0e7490',
    bg1: '#06191e',
    bg2: '#02080a',
    glow: 'rgba(58,154,176,0.32)',
    accent: '#3a9ab0',
  },
  {
    kanji: '鳥',
    memo: '明け方に声を交わす、命のきざしとしての音',
    ink: '#a7f3d0',
    ink2: '#047857',
    bg1: '#071a14',
    bg2: '#030a08',
    glow: 'rgba(95,168,124,0.3)',
    accent: '#5fa87c',
  },
  {
    kanji: '花',
    memo: '散る音はなくとも、季節を告げる静かな気配',
    ink: '#fbcfe8',
    ink2: '#be185d',
    bg1: '#200f17',
    bg2: '#0b0408',
    glow: 'rgba(217,143,166,0.3)',
    accent: '#d98fa6',
  },
  {
    kanji: '叶',
    memo: '願いがかたちになって、静かに響きはじめる',
    ink: '#bbf7d0',
    ink2: '#166534',
    bg1: '#071a10',
    bg2: '#030a06',
    glow: 'rgba(92,156,110,0.3)',
    accent: '#5c9c6e',
  },
  {
    kanji: '謡',
    memo: '言葉に節をつけて、ゆるやかにうたう',
    ink: '#fde68a',
    ink2: '#a16207',
    bg1: '#1e1805',
    bg2: '#0a0802',
    glow: 'rgba(194,160,84,0.3)',
    accent: '#c2a054',
  },
  {
    kanji: '拍',
    memo: '手を打ち、刻む。音に刻まれる区切りとリズム',
    ink: '#fdba74',
    ink2: '#9a3412',
    bg1: '#1f1207',
    bg2: '#0a0503',
    glow: 'rgba(192,122,58,0.3)',
    accent: '#c07a3a',
  },
  {
    kanji: '灯',
    memo: 'ゆらぐ光に、ぬくもりのある音を重ねて',
    ink: '#fcd34d',
    ink2: '#b45309',
    bg1: '#1f1505',
    bg2: '#0a0702',
    glow: 'rgba(224,169,78,0.3)',
    accent: '#e0a94e',
  },
  {
    kanji: '雪',
    memo: '音を吸い込んでしまう、しんとした白の静けさ',
    ink: '#e2e8f0',
    ink2: '#94a3b8',
    bg1: '#131a21',
    bg2: '#06090c',
    glow: 'rgba(184,200,216,0.28)',
    accent: '#b8c8d8',
  },
];
